import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import type { FreeShopItemDef } from '../../../master-data/types/free-shop.types.ts';

// free_shop_item — 무료/광고 수령 상품 카탈로그.
// delivery_items 는 JSONB 이므로 kind:'json' 으로 통째 보존한다.
export const FreeShopItemSchema = defineSchema({
  name: 'serverFreeShopItems',
  fields: [
    ['id', 'string'],
    ['acquire', 'string'],
    ['category', 'string'],
    ['nameKey', 'string'],
    ['descriptionKey', 'string?'],
    ['icon', 'string?'],
    ['displayOrder', 'number'],
    ['deliveryItems', { kind: 'json' }],
    ['purchaseLimit', 'number?'],
    ['periodLimitCount', 'number?'],
    ['periodLimitDays', 'number?'],
    ['startAt', 'number?'],
    ['endAt', 'number?'],
    ['availability', 'string'],
    ['isActive', 'boolean'],
    // append-only (튜플 와이어 순서 계약) — 신규 필드는 항상 끝에 붙인다.
    ['periodLimitHours', 'number?'],
  ],
  lookupKeys: ['id'],
});

export interface RawFreeShopItemRow {
  id: string;
  acquire: string | null;
  category: string | null;
  name_key: string;
  description_key: string | null;
  icon: string | null;
  display_order: number | null;
  delivery_items: Record<string, number> | null;
  purchase_limit: number | null;
  period_limit: string | null;
  start_at: number | null;
  end_at: number | null;
  availability: string | null;
  is_active: boolean;
}

const PERIOD_DAYS: Record<string, number> = { day: 1, week: 7, month: 30 };
/**
 * period_limit 표기. **DB CHECK(`free_shop_item_period_limit_format`)와 문자 그대로 같아야
 * 한다** — 코덱이 더 넓으면 DB 가 막는 값을 코덱이 받아들이고, 더 좁으면 DB 를 통과한 행의
 * 한도가 조용히 사라진다(= 무제한 지급). 한쪽을 고치면 반드시 다른 쪽도 고칠 것.
 * 시간은 1~999 만 허용한다 — 그보다 길면 day/week/month 로 쓰는 게 읽기 쉽고, 상한이
 * 없으면 `1/999999h` 같은 값이 사실상 "평생 1회"인데 기간 한도로 위장한다.
 */
const PERIOD_LIMIT_RE = /^([1-9][0-9]*)\/([1-9][0-9]{0,2}h|day|week|month)$/;
/** 단위 부분이 시간 표기인지. `month` 도 'h' 로 끝나므로 endsWith 로 가르면 안 된다. */
const HOUR_UNIT_RE = /^([1-9][0-9]{0,2})h$/;

/** DB CHECK 가 이미 값을 좁히지만, 구 데이터·다른 authoring 경로가 생겨도 기본값으로 접는다. */
function pickAcquire(v: unknown): FreeShopItemDef['acquire'] {
  return String(v ?? '').trim() === 'ad' ? 'ad' : 'free';
}

function pickAvailability(v: unknown): FreeShopItemDef['availability'] {
  const s = String(v ?? '')
    .trim()
    .toLowerCase();
  return s === 'web2' || s === 'web3' ? s : 'both';
}

export function freeShopItemFromRaw(row: RawFreeShopItemRow): FreeShopItemDef {
  const def: FreeShopItemDef = {
    id: String(row.id),
    acquire: pickAcquire(row.acquire),
    category: (row.category ?? 'PACKAGE') || 'PACKAGE',
    nameKey: String(row.name_key),
    displayOrder: Number(row.display_order ?? 0),
    deliveryItems: row.delivery_items ?? {},
    availability: pickAvailability(row.availability),
    isActive: Boolean(row.is_active),
  };
  const descriptionKey = String(row.description_key ?? '').trim();
  if (descriptionKey) def.descriptionKey = descriptionKey;
  const icon = String(row.icon ?? '').trim();
  if (icon) def.icon = icon;
  if (row.purchase_limit != null && row.purchase_limit > 0) {
    def.purchaseLimit = Number(row.purchase_limit);
  }
  const match = PERIOD_LIMIT_RE.exec(String(row.period_limit ?? '').trim());
  if (match) {
    const count = Number.parseInt(match[1], 10);
    const unit = match[2];
    // 시간 표기와 일/주/월 표기는 배타적이다 — 한 행에 둘 다 남기면 판정부가 어느 단위를
    // 쓸지 모호해진다. 정규식이 둘 중 하나만 매칭시키므로 구조적으로 보장된다.
    const hourMatch = HOUR_UNIT_RE.exec(unit);
    if (hourMatch) {
      def.periodLimitCount = count;
      def.periodLimitHours = Number.parseInt(hourMatch[1], 10);
    } else if (Object.hasOwn(PERIOD_DAYS, unit)) {
      def.periodLimitCount = count;
      def.periodLimitDays = PERIOD_DAYS[unit];
    }
  }
  if (row.start_at != null) def.startAt = Number(row.start_at);
  if (row.end_at != null) def.endAt = Number(row.end_at);
  return def;
}

registerCategorySchema('serverFreeShopItems', FreeShopItemSchema);
